import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';

/**
 * ArchitectureExplorer — browse the live architecture index.
 * Refresh button is production-only.
 */
export default function ArchitectureExplorer({ isProd }) {
  const [snap, setSnap] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = () =>
    api.get('/lab/architecture').then(({ data }) => setSnap(data)).catch(() => {});

  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await api.post('/lab/architecture/refresh');
      await load();
      toast.success('Architecture re-indexed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  if (!snap) return <div className="p-4 text-[11px] text-[var(--txt-secondary)]">loading architecture…</div>;

  const b = snap.backend || {};
  const f = snap.frontend || {};
  const d = snap.database || {};

  return (
    <div data-testid="lab-architecture" className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--txt-secondary)]">
          indexed {short(snap.indexed_at)} · version {snap.version || '—'} · env {snap.environment}
        </div>
        <button
          data-testid="lab-architecture-refresh"
          onClick={refresh}
          disabled={!isProd || refreshing}
          title={isProd ? 'Re-index codebase' : 'Production only'}
          className="flex items-center gap-1.5 border border-cyan-sys text-cyan-sys px-2 py-1 text-[10px] tracking-[0.2em] uppercase hover:bg-cyan-sys hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <RefreshCw size={11} className={refreshing ? 'animate-spin' : ''} />
          re-index
        </button>
      </div>

      <Section title={`Backend · ${b.module_count} modules · ${b.route_count} routes`}>
        <Grid items={b.modules} testid="lab-backend-modules" />
      </Section>

      <Section title="Engines + curated groups">
        <div className="grid md:grid-cols-3 gap-2 text-[11px]">
          {Object.entries(snap.groups || {}).map(([g, mods]) => (
            <div key={g} className="border border-[var(--border-default)] p-2">
              <div className="text-cyan-sys uppercase tracking-[0.2em] text-[10px] mb-1">{g}</div>
              <ul className="text-[10px] font-mono space-y-0.5">
                {mods.map((m) => <li key={m} className="text-[var(--txt-secondary)]">· {m}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      <Section title={`Frontend · ${f.page_count} pages · ${f.component_count} components`}>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="text-[9px] tracking-[0.2em] uppercase text-[var(--txt-secondary)] mb-1">Pages</div>
            <Grid items={f.pages} testid="lab-pages" />
          </div>
          <div>
            <div className="text-[9px] tracking-[0.2em] uppercase text-[var(--txt-secondary)] mb-1">Components</div>
            <Grid items={f.components} testid="lab-components" cap={80} />
          </div>
        </div>
      </Section>

      <Section title={`Database · ${d.collection_count} collections`}>
        <Grid items={d.collections} testid="lab-collections" cap={120} />
      </Section>

      <Section title={`Routes · first 50 of ${b.route_count}`}>
        <div className="overflow-x-auto">
          <table className="w-full text-[10px] font-mono">
            <thead>
              <tr className="text-left text-[var(--txt-secondary)] uppercase tracking-[0.2em] text-[9px]">
                <th className="py-1 pr-3">method</th>
                <th className="py-1 pr-3">path</th>
                <th className="py-1">file</th>
              </tr>
            </thead>
            <tbody>
              {(b.routes || []).slice(0, 50).map((r, i) => (
                <tr key={i} className="border-t border-[var(--border-default)]">
                  <td className="py-1 pr-3 text-cyan-sys">{r.method}</td>
                  <td className="py-1 pr-3">/api{r.path}</td>
                  <td className="py-1 text-[var(--txt-secondary)]">{r.file}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-[var(--border-default)] p-3">
      <div className="text-[10px] tracking-[0.25em] uppercase text-cyan-sys mb-2">// {title}</div>
      {children}
    </div>
  );
}

function Grid({ items, cap = 60, testid }) {
  const list = (items || []).slice(0, cap);
  return (
    <div data-testid={testid} className="flex flex-wrap gap-1 text-[10px] font-mono">
      {list.map((m) => (
        <span key={m} className="border border-[var(--border-default)] px-1.5 py-0.5 text-[var(--txt-secondary)]">
          {m}
        </span>
      ))}
      {(items || []).length > cap && (
        <span className="text-[var(--txt-muted)] italic">… +{items.length - cap} more</span>
      )}
    </div>
  );
}

function short(iso) {
  if (!iso) return '—';
  return String(iso).replace('T', ' ').slice(0, 19) + 'Z';
}
